import { axiosInstance, parseResponse, TENANT_API_URL } from "..";
import { getPublicEnv } from "@/runtime-utils/config/public-env";

const FAMILY_ACCESS_URL = `${TENANT_API_URL}/family-access`;

export type FamilyRelationshipDefinition = { label: string; code: string };

export const getFamilyRelationshipDefinitions = async (): Promise<
  FamilyRelationshipDefinition[]
> => {
  const response = await axiosInstance.get(
    `${FAMILY_ACCESS_URL}/relationships/definitions`
  );
  const result = parseResponse(response);
  if (!result?.success || !Array.isArray(result.data)) {
    throw new Error("Relationship definitions are unavailable");
  }
  const definitions = result.data.filter(
    (item: unknown): item is FamilyRelationshipDefinition =>
      Boolean(item) &&
      typeof item === "object" &&
      typeof (item as FamilyRelationshipDefinition).label === "string" &&
      typeof (item as FamilyRelationshipDefinition).code === "string"
  );
  if (definitions.length !== result.data.length || !definitions.length) {
    throw new Error("Relationship definitions are invalid");
  }
  return definitions;
};

export type FamilyPermissionDomain = string;

export type FamilyDomainDefinition = {
  key: FamilyPermissionDomain;
  label: string;
  recordScope: boolean;
  write: boolean;
};

export type FamilyAccessHistoryEvent = {
  _id: string;
  action: string;
  actor?: { type?: string; name?: string; email?: string };
  domain?: string | null;
  result?: {
    status?: string;
    reason?: string;
    details?: Record<string, unknown>;
  };
  subject?: {
    familyMemberName?: string | null;
    familyMemberEmail?: string | null;
    relationship?: string | null;
    serviceUserName?: string | null;
  };
  createdAt?: string;
};

export const normalizeFamilyDomainDefinitions = (
  value: unknown
): FamilyDomainDefinition[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((entry) => {
    if (!entry || typeof entry !== "object") return [];
    const candidate = entry as Record<string, unknown>;
    const key = normalizeString(candidate.key);
    const label = normalizeString(candidate.label);
    if (!key || !label) return [];
    return [
      {
        key,
        label,
        recordScope: candidate.recordScope === true,
        write: candidate.write === true,
      },
    ];
  });
};

export const hasFamilyAccessSubscription = (
  subscriptionLoaded: boolean,
  allowedPages: string[]
) => !subscriptionLoaded || allowedPages.includes("service/family-access");

export type FamilyAccessScopeRecord = {
  id: string;
  title: string;
  subtitle?: string | null;
  timestamp?: string | null;
  metadata?: Record<string, unknown>;
};

export type FamilyEligibleContact = {
  reference: string;
  name: string;
  email: string;
  relationship: string;
  relationshipLabel: string;
};

export type FamilyChatMember = {
  familyAccountId: string;
  membershipId: string;
  email?: string | null;
  name?: string | null;
  avatar?: string | null;
  relationship?: string | null;
  status?: string | null;
  lastSeen?: string | null;
  unreadCount?: number;
  lastMessageAt?: string | null;
  preview?: string | null;
  isOnline?: boolean;
};

type FamilyChatMembersPayload = {
  members?: unknown;
  chatMembers?: unknown;
  data?: unknown;
};

const normalizeString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const normalizeEmail = (value: unknown): string | null => {
  const normalized = normalizeString(value);
  return normalized ? normalized.toLowerCase() : null;
};

const toFamilyChatMember = (value: unknown): FamilyChatMember | null => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const raw = value as Record<string, unknown>;
  const familyAccountId = normalizeString(raw.familyAccountId);
  const membershipId = normalizeString(raw.membershipId);
  if (!familyAccountId || !membershipId) {
    return null;
  }

  const unreadValue =
    typeof raw.unreadCount === "number"
      ? raw.unreadCount
      : Number(raw.unreadCount ?? 0);

  return {
    familyAccountId,
    membershipId,
    email: normalizeEmail(raw.email),
    name: normalizeString(raw.name),
    avatar: normalizeString(raw.avatar),
    relationship: normalizeString(raw.relationship),
    status: normalizeString(raw.status),
    lastSeen: normalizeString(raw.lastSeen),
    unreadCount: Number.isFinite(unreadValue) ? unreadValue : 0,
    lastMessageAt: normalizeString(raw.lastMessageAt),
    preview: normalizeString(raw.preview),
  };
};

export const normalizeFamilyChatMembers = (
  payload: unknown
): FamilyChatMember[] => {
  const raw = payload as FamilyChatMembersPayload | FamilyChatMember[] | null;

  const candidates = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.members)
      ? raw.members
      : Array.isArray(raw?.chatMembers)
        ? raw.chatMembers
        : Array.isArray(raw?.data)
          ? raw.data
          : [];

  return candidates
    .map((entry) => toFamilyChatMember(entry))
    .filter((entry): entry is FamilyChatMember => {
      if (!entry) {
        return false;
      }

      const normalizedStatus = normalizeString(entry.status)?.toLowerCase();
      if (
        normalizedStatus &&
        ["invited", "pending", "cancelled", "canceled"].includes(
          normalizedStatus
        )
      ) {
        return false;
      }

      return true;
    });
};

export type FamilyChatMessage = {
  _id: string;
  senderId: string;
  receiverId: string;
  message?: string;
  attachment?: unknown;
  createdAt?: string;
  time?: string;
  isRead?: boolean;
  senderName?: string;
  meta?: {
    audience?: "family" | "all_family";
  };
};

export const listFamilyAccessForServiceUser = async (serviceUserId: string) => {
  try {
    const response = await axiosInstance.get(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}`
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const listFamilyAccessScopeRecords = async (
  serviceUserId: string,
  page: { domain?: string; cursor?: string } = {}
) => {
  try {
    const response = await axiosInstance.get(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}/records`,
      { params: page }
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const createFamilyAccessInvite = async (payload: {
  contactReference: string;
  authorizationBasis:
    | "recorded_consent"
    | "verified_legal_authority"
    | "documented_best_interest";
  authorizationConfirmed: true;
  serviceUserId: string;
  expiresInHours?: number;
  notes?: string;
}) => {
  try {
    const response = await axiosInstance.post(
      `${FAMILY_ACCESS_URL}/invite`,
      payload
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const grantFamilyAccessPermissions = async (payload: {
  policyVersion: 2;
  membershipId: string;
  serviceUserId: string;
  expectedTenantPolicyVersion: number;
  expectedPermissionId?: string;
  expectedRevision?: number;
  expectedServiceUserPolicyRevision: number | null;
  memberAccessMode: FamilyMemberAccessMode;
  // Omitted for inheriting members, which follow the person policy.
  grants?: Array<{ domain: string; access: Array<"read" | "write"> }>;
  relativeRestrictions: FamilyPolicyRestrictions;
}) => {
  try {
    const response = await axiosInstance.post(
      `${FAMILY_ACCESS_URL}/permissions/grant`,
      payload
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export type FamilyPolicyRestrictions = {
  disabledDomains?: string[];
  hiddenFields?: Array<{ domain: string; fieldKeys: string[] }>;
  hiddenCategoryIds?: string[];
  hiddenTaskIds?: string[];
  sinceDate?: string | null;
  excludedRecords?: Array<{ domain: string; recordIds: string[] }>;
  domainAccess?: Array<{ domain: string; access: Array<"read" | "write"> }>;
  expiresAt?: string | null;
};
// Absent means explicit; inherit_person follows the current person policy.
export type FamilyMemberAccessMode = "explicit" | "inherit_person";
export type FamilyV2Permission = {
  _id: string;
  __v: number;
  policyVersion: 2;
  memberAccessMode?: FamilyMemberAccessMode;
  membershipId: string;
  permissions: Array<{ domain: string; access: Array<"read" | "write"> }>;
  serviceUserRestrictions: FamilyPolicyRestrictions;
  relativeRestrictions: FamilyPolicyRestrictions;
  consent?: { status?: "pending" | "accepted" | "revoked" };
  revokedAt?: string | null;
};
export type FamilyPermissionEditorContext = {
  policyVersion: 2;
  tenantPolicyVersion: number;
  domains: Record<
    string,
    { access: Array<"read" | "write">; fieldKeys: string[] }
  >;
  sinceDate: string | null;
  definitions: Array<{ key: string; label: string }>;
  categories: Array<{ id: string; title: string; logo?: string }>;
  tasks: Array<{
    id: string;
    title: string;
    categoryId: string;
    logo?: string;
  }>;
  serviceUserId: string;
  personPolicy: {
    revision: number;
    tenantPolicyVersion: number;
    restrictions: FamilyPolicyRestrictions;
  } | null;
};
export async function saveFamilyServiceUserPolicy(
  serviceUserId: string,
  payload: {
    policyVersion: 2;
    expectedTenantPolicyVersion: number;
    expectedRevision: number | null;
    restrictions: FamilyPolicyRestrictions;
  }
) {
  try {
    return parseResponse(
      await axiosInstance.put(
        `${FAMILY_ACCESS_URL}/service-user/${encodeURIComponent(serviceUserId)}/person-policy`,
        payload
      )
    );
  } catch (error: any) {
    return parseResponse(error.response);
  }
}
export async function getFamilyPermissionEditorContext(
  serviceUserId: string
): Promise<FamilyPermissionEditorContext> {
  const result = parseResponse(
    await axiosInstance.get(
      `${FAMILY_ACCESS_URL}/service-user/${encodeURIComponent(serviceUserId)}/permission-policy`
    )
  );
  if (
    !result?.success ||
    result.data?.policyVersion !== 2 ||
    result.data?.serviceUserId !== serviceUserId ||
    !Number.isInteger(result.data.tenantPolicyVersion)
  )
    throw new Error(
      result?.message || "Published Family policy is unavailable"
    );
  return result.data;
}

export const revokeFamilyAccessPermissions = async (payload: {
  familyAccountId: string;
  serviceUserId: string;
  orchestrationId: string;
  reason?: string;
}) => {
  try {
    const response = await axiosInstance.post(
      `${FAMILY_ACCESS_URL}/permissions/revoke`,
      payload
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const requestFamilyAccountPasswordReset = async (
  serviceUserId: string,
  familyAccountId: string
) => {
  try {
    const response = await axiosInstance.post(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}/account/${familyAccountId}/password-reset`
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const revokeFamilyAccountSecurity = async (
  serviceUserId: string,
  familyAccountId: string,
  reason: string
) => {
  try {
    const response = await axiosInstance.post(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}/account/${familyAccountId}/revoke-security`,
      { reason }
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export type FamilyAccountSecurity = {
  activeSessionCount: number;
  sessions: Array<{
    sessionId: string;
    platform: "web" | "ios" | "android";
    createdAt: string;
    lastUsedAt: string;
  }>;
  passkeys: Array<{
    credentialId: string;
    displayName: string;
    createdAt: string;
    lastUsedAt?: string | null;
  }>;
};

export const revokeFamilyAccountSession = async (
  serviceUserId: string,
  familyAccountId: string,
  sessionId: string,
  reason: string
) => {
  try {
    const response = await axiosInstance.post(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}/account/${familyAccountId}/sessions/${encodeURIComponent(sessionId)}/revoke`,
      { reason }
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const getFamilyAccountSecurity = async (
  serviceUserId: string,
  familyAccountId: string
) => {
  try {
    const response = await axiosInstance.get(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}/account/${familyAccountId}/security`
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const revokeFamilyAccountPasskey = async (
  serviceUserId: string,
  familyAccountId: string,
  credentialId: string,
  reason: string
) => {
  try {
    const response = await axiosInstance.post(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}/account/${familyAccountId}/passkeys/${encodeURIComponent(credentialId)}/revoke`,
      { reason }
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const resendFamilyAccessInvite = async (
  inviteId: string,
  serviceUserId: string
) => {
  try {
    const response = await axiosInstance.post(
      `${FAMILY_ACCESS_URL}/invite/${inviteId}/resend`,
      { serviceUserId }
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const listFamilyAccessHistory = async (serviceUserId: string) => {
  try {
    const response = await axiosInstance.get(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}/history`
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const cancelFamilyAccessInvite = async (
  inviteId: string,
  serviceUserId: string
) => {
  try {
    const response = await axiosInstance.post(
      `${FAMILY_ACCESS_URL}/invite/${inviteId}/cancel`,
      { serviceUserId }
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const getFamilyPortalUrl = (): string | undefined => {
  const configured = getPublicEnv("NEXT_PUBLIC_FAMILY_WEB_URL").trim();
  const destination =
    configured ||
    (process.env.NODE_ENV !== "production" ? "http://localhost:8081" : "");
  try {
    const url = new URL(destination);
    if (
      !["https:", "http:"].includes(url.protocol) ||
      url.username ||
      url.password
    ) {
      return undefined;
    }
    return url.href;
  } catch {
    return undefined;
  }
};

export const listFamilyChatMembers = async (serviceUserId: string) => {
  try {
    const response = await axiosInstance.get(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}/chat-members`
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const getFamilyChatMessages = async (
  serviceUserId: string,
  familyAccountId: string
) => {
  try {
    const response = await axiosInstance.get(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}/chat/${familyAccountId}`
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const markFamilyChatRead = async (
  serviceUserId: string,
  familyAccountId: string
) => {
  try {
    const response = await axiosInstance.put(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}/chat/${familyAccountId}/read`
    );
    const result = parseResponse(response);
    if (result?.success && typeof window !== "undefined")
      window.dispatchEvent(
        new CustomEvent("wisterias:messages-read", {
          detail: { scope: "family" },
        })
      );
    return result;
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export const sendFamilyChatMessage = async (
  serviceUserId: string,
  familyAccountId: string,
  payload: {
    message?: string;
    attachment?: { file?: Blob; name?: string } | Blob | null;
    audience?: "family" | "all_family";
  }
) => {
  try {
    const formData = createFamilyChatFormData(payload);
    const response = await axiosInstance.post(
      `${FAMILY_ACCESS_URL}/service-user/${serviceUserId}/chat/${familyAccountId}`,
      formData
    );
    return parseResponse(response);
  } catch (error: any) {
    return parseResponse(error.response);
  }
};

export function createFamilyChatFormData(payload: {
  message?: string;
  attachment?: { file?: Blob; name?: string } | Blob | null;
  audience?: "family" | "all_family";
}) {
  const formData = new FormData();
  formData.append("message", payload.message || "");
  formData.append("audience", payload.audience || "family");
  const candidate = payload.attachment;
  const file = candidate instanceof Blob ? candidate : candidate?.file;
  if (file) {
    const fileName =
      candidate && typeof (candidate as { name?: unknown }).name === "string"
        ? String((candidate as { name: string }).name)
        : "attachment";
    formData.append("attachment", file, fileName);
  }
  return formData;
}
